import { Flex, Select, Typography } from 'antd';

const { Text } = Typography;

/**
 * Drawer header for the filter editor: the editor's name, plus the matrix mode's
 * band/threshold property pickers.
 *
 * The pickers sit in the header rather than in MatrixEditor's body because they
 * are the two axes the entire grid is derived from — one choice each, changed
 * rarely — while the body is reserved for the two things being compared side by
 * side: the threshold grid (what the ranges are) and the cost summary (what they
 * cost). Keeping them here also means the axes stay visible while the body
 * scrolls, so a wide grid never leaves you guessing what its rows and columns are.
 *
 * Simple mode puts its single `property` picker here for the same reason: it is
 * the one axis every class range is measured on, so it belongs with the title
 * rather than among the ranges it governs.
 *
 * Rendered into antd's `Drawer title` slot, so this returns an inline row rather
 * than a block section.
 *
 * @param {object} props
 * @param {boolean} props.isSimple - whether the active filter is simple (1D)
 * @param {object} props.filter - active matrix/simple filter config
 * @param {(updates: object) => void} props.updateFilter
 * @param {Array<{id,label,unit}>} props.availableProperties
 */
const FilterEditorHeader = ({ isSimple, filter, updateFilter, availableProperties = [] }) => {
	const propertyOptions = availableProperties.map(p => ({ value: p.id, label: p.label }));

	return (
		// space-between pushes the axis pickers to the right edge, away from the
		// title. Full width because antd's title slot is a flex child that would
		// otherwise shrink to its content and leave the pickers mid-header.
		<Flex align="center" justify="space-between" gap={16} wrap="wrap" style={{ width: '100%' }}>
			<Text strong style={{ fontSize: '0.9rem' }}>
				{isSimple ? 'Simple Filter Editor' : 'Matrix Filter Editor'}
			</Text>

			{isSimple ? (
				<Flex align="center" gap={6}>
					{/* Visible caption plus an aria-label on the control: the caption
					    isn't a <label>, so it doesn't name the Select on its own. */}
					<Text type="secondary" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
						Property
					</Text>
					<Select
						size="small"
						value={filter?.property}
						onChange={property => updateFilter({ property })}
						options={propertyOptions}
						showSearch
						optionFilterProp="label"
						style={{ minWidth: 150 }}
						aria-label="Classification property"
					/>
				</Flex>
			) : (
				<Flex align="center" gap={16} wrap="wrap">
					<Flex align="center" gap={6}>
						<Text
							type="secondary"
							style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
							Bands
						</Text>
						<Select
							size="small"
							value={filter?.bandProperty}
							onChange={bandProperty => updateFilter({ bandProperty })}
							options={propertyOptions}
							showSearch
							optionFilterProp="label"
							style={{ minWidth: 150 }}
							aria-label="Band property (grid rows)"
						/>
					</Flex>
					<Flex align="center" gap={6}>
						<Text
							type="secondary"
							style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
							Thresholds
						</Text>
						<Select
							size="small"
							value={filter?.thresholdProperty}
							onChange={thresholdProperty => updateFilter({ thresholdProperty })}
							options={propertyOptions}
							showSearch
							optionFilterProp="label"
							style={{ minWidth: 150 }}
							aria-label="Threshold property (grid columns)"
						/>
					</Flex>
				</Flex>
			)}
		</Flex>
	);
};

export default FilterEditorHeader;
